/**
 * Module 46 (WP-46.1) — the languages this product ships, stated once.
 *
 * ## Why a registry rather than three lists
 *
 * Before this, "which languages exist" was written down in three unrelated
 * places that had to be kept in agreement by memory: each platform profile's
 * `capabilities.locales`, `i18n/index.ts`'s `resources` map, and — hardcoded as
 * two literal buttons — the Settings popup. Two languages made that survivable.
 * Nine does not: the failure mode is a language that resolves but has no
 * translation bundle, or one that has a bundle and no way to pick it, and
 * neither is visible from the file you happen to be editing.
 *
 * This module is the single source of truth all three read. Adding a language is
 * an entry here plus a translation file — nothing else. That is the whole point,
 * and it is what makes Tier 2/3 (Indonesian, Dutch, Russian, … ) cheap later
 * without touching the resolver or the Settings UI again.
 *
 * ## Why `nativeName` is data and not a translation key
 *
 * A language's own name does not change with the UI's current language: Hindi is
 * हिन्दी whether the app is currently speaking English or Turkish. Running these
 * through `t()` would mean nine names × nine locales = 81 strings restating the
 * same nine facts, and every one of them a chance to be wrong in a language
 * nobody on the team reads. The picker shows `nativeName` verbatim, which is
 * also what lets a player who cannot read the current UI language still find
 * their own.
 *
 * `englishName` is *not* shown to players. It exists so this file reads
 * unambiguously to a maintainer who cannot read the native script — which,
 * given the list below, is everyone.
 *
 * ## Codes
 *
 * BCP-47. Most are bare primary subtags; `pt-BR` is region-qualified because the
 * Portuguese being shipped is specifically Brazilian, which is the CrazyGames
 * audience that motivated it. `resolveLocale` handles both shapes — see its own
 * doc for how a region-qualified entry is matched.
 */
/**
 * Tier 1, in the order the picker displays them.
 *
 * **English is first and that is load-bearing**, not alphabetical luck:
 * `resolveLocale` treats the first supported entry as the required fallback, and
 * CrazyGames requires English specifically. `languages.test.ts` pins it.
 *
 * The rest are ordered by the reach that put them in Tier 1 — Hindi first as the
 * product's existing second language, then the European set a CrazyGames launch
 * targets. Russian (Module 148) is last so no existing player's list moves; it
 * opens Playhop / Yandex Games, which requires it.
 */
export const LANGUAGE_REGISTRY = Object.freeze([
    { code: 'en', englishName: 'English', nativeName: 'English' },
    { code: 'hi', englishName: 'Hindi', nativeName: 'हिन्दी' },
    { code: 'es', englishName: 'Spanish', nativeName: 'Español' },
    { code: 'pt-BR', englishName: 'Portuguese (Brazil)', nativeName: 'Português (Brasil)' },
    { code: 'de', englishName: 'German', nativeName: 'Deutsch' },
    { code: 'fr', englishName: 'French', nativeName: 'Français' },
    { code: 'pl', englishName: 'Polish', nativeName: 'Polski' },
    { code: 'tr', englishName: 'Turkish', nativeName: 'Türkçe' },
    { code: 'it', englishName: 'Italian', nativeName: 'Italiano' },
    { code: 'ru', englishName: 'Russian', nativeName: 'Русский' },
]);
/**
 * Just the codes, in registry order — what a platform profile's `locales` and
 * i18next's resource map are made of.
 *
 * Derived rather than written out again, because a second literal list is
 * exactly the drift this module exists to remove.
 */
export const LANGUAGE_CODES = Object.freeze(LANGUAGE_REGISTRY.map((l) => l.code));
/**
 * The registry entry for a code, or `undefined` when nothing ships it.
 *
 * `undefined` rather than a defaulted entry: the caller that renders a name and
 * the caller that validates a stored value want different answers to "this code
 * is unknown", and only the former should quietly substitute English.
 */
export function findLanguage(code) {
    if (!code)
        return undefined;
    const wanted = code.trim().toLowerCase();
    return LANGUAGE_REGISTRY.find((l) => l.code.toLowerCase() === wanted);
}
